(() => {
  const CUP_TRANSFORMS = {
    '4oz': 'translate(64 78) scale(.8 .74)',
    '6oz': 'translate(42 55) scale(.87 .84)',
    '8oz': 'translate(22 30) scale(.93 .92)',
    '10oz': 'translate(10 15) scale(.97 .97)',
    '12oz': 'translate(0 0) scale(1 1)',
    '16oz': 'translate(-14 -18) scale(1.045 1.08)',
  };

  class LccCupVisualiser {
    constructor(root) {
      this.root = root;
      this.svg = root.querySelector('[data-cup-svg]');
      this.cupGroup = root.querySelector('[data-cup-group]');
      this.logo = root.querySelector('[data-uploaded-logo]');
      this.placeholder = root.querySelector('[data-logo-placeholder]');
      this.fileInput = root.querySelector('[data-logo-input]');
      this.fileName = root.querySelector('[data-file-name]');
      this.removeButton = root.querySelector('[data-remove-logo]');
      this.positionControls = root.querySelector('[data-position-controls]');
      this.scaleInput = root.querySelector('[data-logo-scale]');
      this.scaleOutput = root.querySelector('[data-scale-output]');
      this.sizeOutput = root.querySelector('[data-size-output]');
      this.sizeBadge = root.querySelector('[data-size-badge]');
      this.caption = root.querySelector('[data-preview-caption]');
      this.status = root.querySelector('[data-visualiser-status]');
      this.size = '8oz';
      this.logoCenter = { x: 320, y: 365 };
      this.dragOffset = { x: 0, y: 0 };
      this.dragging = false;
      this.hasLogo = false;

      this.bindEvents();
      this.setSize('8oz');
      this.updateLogo();
      root.dataset.visualiserReady = 'true';
    }

    bindEvents() {
      this.root.querySelectorAll('[data-cup-size]').forEach((button) => {
        button.addEventListener('click', () => this.setSize(button.dataset.cupSize));
      });

      this.fileInput.addEventListener('change', () => this.loadLogo());
      this.removeButton.addEventListener('click', () => this.removeLogo());
      this.scaleInput.addEventListener('input', () => this.updateLogo());
      this.root.querySelector('[data-reset-logo]').addEventListener('click', () => this.resetLogo());

      this.root.querySelectorAll('[data-nudge-x]').forEach((button) => {
        button.addEventListener('click', () => {
          this.logoCenter.x += Number(button.dataset.nudgeX || 0);
          this.logoCenter.y += Number(button.dataset.nudgeY || 0);
          this.constrainLogo();
          this.updateLogo();
          this.status.textContent = 'Artwork position adjusted.';
        });
      });

      this.logo.addEventListener('pointerdown', (event) => this.startDrag(event));
      this.logo.addEventListener('pointermove', (event) => this.dragLogo(event));
      this.logo.addEventListener('pointerup', (event) => this.endDrag(event));
      this.logo.addEventListener('pointercancel', (event) => this.endDrag(event));
    }

    setSize(size) {
      if (!CUP_TRANSFORMS[size]) return;
      this.size = size;
      this.cupGroup.setAttribute('transform', CUP_TRANSFORMS[size]);
      this.root.querySelectorAll('[data-cup-size]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.cupSize === size));
      });
      this.sizeOutput.textContent = `Selected size: ${size}`;
      this.sizeBadge.textContent = `${size} · white`;
      this.caption.textContent = `${size} cup · Front print position`;
      this.svg.setAttribute('aria-label', `Interactive preview of a ${size} white paper cup`);
      this.status.textContent = this.hasLogo
        ? `${size} selected. Drag your logo on the cup to refine its position.`
        : `${size} selected. Upload a logo to continue.`;
    }

    loadLogo() {
      const file = this.fileInput.files && this.fileInput.files[0];
      if (!file) return;

      const supported = ['image/png', 'image/jpeg', 'image/webp'];
      if (!supported.includes(file.type)) {
        this.fileInput.value = '';
        this.status.textContent = 'Please choose a PNG, JPG or WEBP image for this demo.';
        return;
      }

      if (file.size > 8 * 1024 * 1024) {
        this.fileInput.value = '';
        this.status.textContent = 'That file is larger than 8MB. Please choose a smaller image for this demo.';
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        this.logo.setAttribute('href', reader.result);
        this.logo.removeAttribute('hidden');
        this.placeholder.setAttribute('hidden', '');
        this.positionControls.disabled = false;
        this.removeButton.disabled = false;
        this.fileName.textContent = file.name;
        this.hasLogo = true;
        this.resetLogo(false);
        this.status.textContent = `${file.name} is on the ${this.size} cup. Drag it directly on the cup or use the position controls.`;
      });
      reader.addEventListener('error', () => {
        this.status.textContent = 'The logo could not be read. Please try another image.';
      });
      reader.readAsDataURL(file);
    }

    removeLogo() {
      this.fileInput.value = '';
      this.logo.removeAttribute('href');
      this.logo.setAttribute('hidden', '');
      this.placeholder.removeAttribute('hidden');
      this.positionControls.disabled = true;
      this.removeButton.disabled = true;
      this.fileName.textContent = 'PNG, JPG or WEBP · transparent artwork works best';
      this.hasLogo = false;
      this.resetLogo(false);
      this.status.textContent = 'Artwork removed. Upload another logo whenever you are ready.';
    }

    resetLogo(announce = true) {
      this.logoCenter = { x: 320, y: 365 };
      this.scaleInput.value = '0.68';
      this.updateLogo();
      if (announce) this.status.textContent = 'Artwork scale and position reset.';
    }

    updateLogo() {
      const scale = Number(this.scaleInput.value);
      const size = 220 * scale;
      this.scaleOutput.textContent = `${Math.round(scale * 100)}%`;
      this.logo.setAttribute('x', String(this.logoCenter.x - size / 2));
      this.logo.setAttribute('y', String(this.logoCenter.y - size / 2));
      this.logo.setAttribute('width', String(size));
      this.logo.setAttribute('height', String(size));
    }

    constrainLogo() {
      this.logoCenter.x = Math.min(455, Math.max(185, this.logoCenter.x));
      this.logoCenter.y = Math.min(545, Math.max(225, this.logoCenter.y));
    }

    pointInCup(event) {
      const point = this.svg.createSVGPoint();
      point.x = event.clientX;
      point.y = event.clientY;
      const matrix = this.cupGroup.getScreenCTM();
      return matrix ? point.matrixTransform(matrix.inverse()) : { x: 320, y: 365 };
    }

    startDrag(event) {
      if (!this.hasLogo) return;
      event.preventDefault();
      const point = this.pointInCup(event);
      this.dragOffset = {
        x: point.x - this.logoCenter.x,
        y: point.y - this.logoCenter.y,
      };
      this.dragging = true;
      this.logo.classList.add('is-dragging');
      this.logo.setPointerCapture(event.pointerId);
    }

    dragLogo(event) {
      if (!this.dragging) return;
      event.preventDefault();
      const point = this.pointInCup(event);
      this.logoCenter.x = point.x - this.dragOffset.x;
      this.logoCenter.y = point.y - this.dragOffset.y;
      this.constrainLogo();
      this.updateLogo();
    }

    endDrag(event) {
      if (!this.dragging) return;
      this.dragging = false;
      this.logo.classList.remove('is-dragging');
      if (this.logo.hasPointerCapture(event.pointerId)) this.logo.releasePointerCapture(event.pointerId);
      this.status.textContent = 'Artwork position updated.';
    }
  }

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-lcc-cup-visualiser]:not([data-visualiser-ready])').forEach((root) => {
      new LccCupVisualiser(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
